import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCw, ChevronLeft, Clock, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../common/Button';
import type { CompetitionDetail } from '../../types';

interface SubstitutePlayerModalProps {
  detail: CompetitionDetail | null;
  isOpen: boolean;
  onClose: () => void;
  /** The dupla being edited (opened from its pencil button). */
  teamId: string | null;
  onSubmit: (teamId: string, outPlayerId: string, inPlayerId: string) => Promise<void>;
  /** Remove the whole dupla (players back to the queue). Pre-play only; the
   *  parent passes it so the red "Remover dupla" action appears when eligible. */
  onRemove?: (teamId: string) => Promise<void>;
}

/**
 * Two-step admin substitution, scoped to one dupla:
 *   1. Show the dupla's two players, each with a swap button.
 *   2. Pick who leaves → "Substituir {nome} por" + two groups of candidates:
 *        - the queue (waitlist) → a substitution;
 *        - players already in other duplas → a swap (the two trade teams), for
 *          manual balancing after the draw.
 * The dupla(s) keep their identity/history — only the people change. On a pooled
 * draw, each name shows its list letter (A/B/C) to help balance.
 */
export function SubstitutePlayerModal({
  detail,
  isOpen,
  onClose,
  teamId,
  onSubmit,
  onRemove,
}: SubstitutePlayerModalProps) {
  const [outPlayerId, setOutPlayerId] = useState<string | null>(null);
  const [inPlayerId, setInPlayerId] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Reset to step 1 whenever the modal (re)opens for a dupla.
  useEffect(() => {
    if (isOpen) {
      setOutPlayerId(null);
      setInPlayerId(null);
      setConfirmingRemove(false);
      setError(null);
    }
  }, [isOpen, teamId]);

  const team = useMemo(
    () => (teamId ? detail?.teams.find((t) => t.id === teamId) ?? null : null),
    [detail, teamId]
  );
  const queue = useMemo(
    () => (detail ? detail.players.filter((p) => p.status === 'waitlisted') : []),
    [detail]
  );
  // Players already in other duplas — picking one performs a swap (trade teams).
  const others = useMemo(
    () =>
      detail && teamId
        ? detail.players.filter((p) => p.status === 'active' && p.teamId && p.teamId !== teamId)
        : [],
    [detail, teamId]
  );
  const teamNumberById = useMemo(() => {
    const m = new Map<string, number>();
    detail?.teams.forEach((t) => m.set(t.id, t.teamNumber));
    return m;
  }, [detail]);

  if (!detail) return null;

  const showPools = !!detail.poolCount;
  const poolTag = (pool: 'A' | 'B' | 'C' | null) => (showPools && pool ? ` (${pool})` : '');
  const isSwap = inPlayerId != null && others.some((p) => p.id === inPlayerId);

  const outPlayer = team?.players.find((p) => p.id === outPlayerId) ?? null;
  const outName = outPlayer?.name || 'jogador';

  // Removing a whole dupla is only safe before any match is played; the group
  // must also keep at least two duplas (otherwise the W.O. flow is used). The
  // backend enforces both — this only governs whether the action is offered.
  const anyMatchPlayed = detail.teams.some((t) => t.matchesPlayed > 0);
  const groupSize = team?.groupId
    ? detail.teams.filter((t) => t.groupId === team.groupId).length
    : 0;
  const groupWouldKeepTwo = groupSize - 1 >= 2;
  const canRemove =
    !!onRemove && !!team?.groupId && detail.status === 'group_stage' && !anyMatchPlayed;

  const goBack = () => {
    setOutPlayerId(null);
    setConfirmingRemove(false);
    setError(null);
  };

  const handleRemoveClick = () => {
    setError(null);
    if (!groupWouldKeepTwo) {
      setError('O grupo ficaria com menos de duas duplas. Use o W.O. para esta dupla.');
      return;
    }
    setConfirmingRemove(true);
  };

  const handleRemove = async () => {
    if (!teamId || !onRemove) return;
    setError(null);
    setIsRemoving(true);
    try {
      await onRemove(teamId);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erro ao remover dupla');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleConfirm = async () => {
    if (!teamId || !outPlayerId || !inPlayerId) return;
    setError(null);
    setIsSaving(true);
    try {
      await onSubmit(teamId, outPlayerId, inPlayerId);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erro ao substituir jogador');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && team && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-5 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {(outPlayerId || confirmingRemove) && (
                  <button
                    onClick={goBack}
                    className="p-1 -ml-1 text-white/60 hover:text-white rounded-lg transition-colors shrink-0"
                    aria-label="Voltar"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <h3 className="font-display text-lg font-bold text-white truncate">
                  {outPlayerId
                    ? `Substituir ${outName} por`
                    : confirmingRemove
                      ? `Remover Dupla ${team.teamNumber}`
                      : `Dupla ${team.teamNumber}`}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 -mr-1 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1 — choose who leaves the dupla (or remove the whole dupla) */}
            {!outPlayerId && !confirmingRemove && (
              <div className="space-y-2">
                {team.players.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setOutPlayerId(p.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                  >
                    <span className="flex-1 text-left text-sm text-white truncate">
                      {p.name || 'Jogador'}
                      {poolTag(p.pool)}
                    </span>
                    <span
                      className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-turquoise/20 text-turquoise"
                      aria-label="Substituir"
                    >
                      <RotateCw className="w-4 h-4" />
                    </span>
                  </button>
                ))}

                {canRemove && (
                  <div className="pt-3 mt-1 border-t border-white/10">
                    <button
                      onClick={handleRemoveClick}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm bg-coral/10 text-coral border border-coral/30 hover:bg-coral/20 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remover dupla
                    </button>
                    {error && <p className="text-coral text-sm mt-3">{error}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Remove confirmation */}
            {confirmingRemove && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-coral/10 border border-coral/30">
                  <AlertTriangle className="w-5 h-5 text-coral shrink-0 mt-0.5" />
                  <p className="text-sm text-white/80">
                    Os jogos não disputados desta dupla serão apagados e os dois jogadores voltarão
                    para a fila de espera (continuam com acesso ao torneio).
                  </p>
                </div>
                {error && <p className="text-coral text-sm">{error}</p>}
                <div className="flex gap-2">
                  <Button variant="secondary" fullWidth onClick={goBack} disabled={isRemoving}>
                    Cancelar
                  </Button>
                  <button
                    onClick={handleRemove}
                    disabled={isRemoving}
                    className="flex-1 inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-6 py-3 text-base bg-coral text-white hover:bg-coral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRemoving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Remover dupla'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 — pick who comes in: from the queue (substitution) or
                from another dupla (swap). */}
            {outPlayerId && (
              queue.length === 0 && others.length === 0 ? (
                <div className="text-center py-6">
                  <Clock className="w-7 h-7 text-white/30 mx-auto mb-3" />
                  <p className="text-sm text-white/70">Ninguém disponível</p>
                  <p className="text-[11px] text-white/40 mt-1 max-w-xs mx-auto">
                    Convide alguém para o torneio (entra na fila) ou sorteie mais de uma dupla para
                    poder trocar jogadores.
                  </p>
                </div>
              ) : (
                <>
                  {queue.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">
                        Fila de espera
                      </p>
                      <div className="space-y-1.5">
                        {queue.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => setInPlayerId(p.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition-colors ${
                              inPlayerId === p.id
                                ? 'bg-turquoise/20 border-turquoise/50 text-turquoise'
                                : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                            }`}
                          >
                            <span className="truncate">
                              {p.name || 'Jogador'}
                              {poolTag(p.pool)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {others.length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1.5">
                        Em outras duplas (trocar)
                      </p>
                      <div className="space-y-1.5">
                        {others.map((p) => {
                          const teamNumber = p.teamId ? teamNumberById.get(p.teamId) : undefined;
                          return (
                            <button
                              key={p.id}
                              onClick={() => setInPlayerId(p.id)}
                              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm border transition-colors ${
                                inPlayerId === p.id
                                  ? 'bg-turquoise/20 border-turquoise/50 text-turquoise'
                                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                              }`}
                            >
                              <span className="truncate">
                                {p.name || 'Jogador'}
                                {poolTag(p.pool)}
                              </span>
                              {teamNumber !== undefined && (
                                <span className="shrink-0 text-[11px] text-white/40">
                                  Dupla {teamNumber}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {error && <p className="text-coral text-sm mt-4">{error}</p>}

                  <Button
                    onClick={handleConfirm}
                    fullWidth
                    isLoading={isSaving}
                    disabled={!inPlayerId}
                    className="mt-5"
                  >
                    {isSwap ? 'Confirmar troca' : 'Confirmar substituição'}
                  </Button>
                </>
              )
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
